import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Collapse,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  CircularProgress,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Description as DescriptionIcon,
  CheckBox as CheckBoxIcon,
  List as ListIcon,
  Reply as ReplyIcon,
  Apps as AppsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ToggleOn,
  ToggleOff,
} from '@mui/icons-material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS } from '../../helpers/constants';

export interface RealTimeNotificationSettingsResponse {
  id?: string;
  user_id?: string;
  workspace_id?: string;
  channel_id?: string;
  is_notifications_enabled?: boolean;
  apps_enabled?: string[];
  is_post_enabled?: boolean;
  is_mention_enabled?: boolean;
  is_task_enabled?: boolean;
  is_approval_enabled?: boolean;
  is_new_email_enabled?: boolean;
  is_email_reply_enabled?: boolean;
  created_at?: string;
  updated_at?: string;
}

interface NotificationsTabProps {
  channelId?: string;
  workspaceId?: string;
}

const NotificationsTab: React.FC<NotificationsTabProps> = ({ channelId, workspaceId }) => {
  const muiTheme = useMuiTheme();
  const { isDarkMode } = useTheme();
  const { user, token } = useAuth();
  const { showSuccess, showError } = useToast();
  // Resolve userId: use auth context first, then localStorage (same as ApprovalsPage, TaskAssignmentPanel, etc.)
  const userId = useMemo(() => {
    if (user?.id) return user.id;
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      return stored ? (JSON.parse(stored) as { user_id?: string }).user_id ?? '' : '';
    } catch {
      return '';
    }
  }, [user?.id]);

  // State for mute all notifications
  const [muteAll, setMuteAll] = useState(false);

  // State for email notifications section collapse
  const [emailNotificationsOpen, setEmailNotificationsOpen] = useState(true);

  // State for individual email notification toggles
  const [emailNotifications, setEmailNotifications] = useState({
    posts: true,
    approvals: false,
    tasks: true,
    mentions: true,
    replies: true,
    apps: true,
  });

  // State for apps dropdown
  const [selectedApps, setSelectedApps] = useState<string>('');

  // Track if user has saved at least once (Show "Save" first time, "Update" thereafter)
  const [hasSavedOnce, setHasSavedOnce] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // workspace_id from GET response, used when saving (fallback: workspaceId prop)
  const [workspaceIdFromApi, setWorkspaceIdFromApi] = useState<string | null>(null);
  // id from GET/save response, required for Update API
  const [settingsId, setSettingsId] = useState<string | null>(null);

  // Fetch real-time notification settings when user opens the Notifications tab (runs when tab is selected so the request appears in Network tab)
  // Use same token source as rest of app: REFRESH_TOKEN then AUTH_TOKEN (channelService, ApprovalsPage, ThreadDetailPage, etc. all use REFRESH_TOKEN for API calls)
  useEffect(() => {
    if (!channelId || !userId) return;

    const fetchSettings = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const authToken =
          localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) ||
          localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) ||
          '';
        const url = `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.NOTIFICATIONS.REAL_TIME_SETTINGS(userId, channelId)}`;
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (authToken) headers.Authorization = `Bearer ${authToken}`;
        const response = await fetch(url, {
          method: 'GET',
          headers,
        });
        if (!response.ok) {
          const errText = await response.text();
          throw new Error(errText || `Failed to load settings (${response.status})`);
        }
        const data: RealTimeNotificationSettingsResponse = await response.json();

        // Map API response to local state
        setMuteAll(!(data.is_notifications_enabled ?? true));
        setEmailNotifications((prev) => ({
          ...prev,
          posts: data.is_post_enabled ?? data.is_new_email_enabled ?? prev.posts,
          approvals: data.is_approval_enabled ?? prev.approvals,
          tasks: data.is_task_enabled ?? prev.tasks,
          mentions: data.is_mention_enabled ?? prev.mentions,
          replies: data.is_email_reply_enabled ?? prev.replies,
        }));
        if (Array.isArray(data.apps_enabled) && data.apps_enabled.length > 0) {
          setSelectedApps(data.apps_enabled[0] ?? '');
        }
        if (data.workspace_id) setWorkspaceIdFromApi(data.workspace_id);
        if (data.id) setSettingsId(data.id);
        // If we got an existing record, user has already saved once
        setHasSavedOnce(!!data.id);
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : 'Failed to load notification settings');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [userId, channelId, token]);

  const handleMuteAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMuteAll(event.target.checked);
  };

  const handleEmailNotificationChange = (key: keyof typeof emailNotifications) => {
    setEmailNotifications((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleAppsChange = (event: SelectChangeEvent<string>) => {
    setSelectedApps(event.target.value);
  };

  const handleSaveOrUpdate = async () => {
    if (!channelId) return;
    const effectiveWorkspaceId = workspaceIdFromApi ?? workspaceId ?? '';
    if (!effectiveWorkspaceId) {
      setSaveError('Workspace is required to save notification settings.');
      return;
    }
    const isUpdate = hasSavedOnce && !!settingsId;
    setSaveError(null);
    setSaving(true);
    try {
      const authToken =
        localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) ||
        localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) ||
        '';
      const url = isUpdate
        ? `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.NOTIFICATIONS.UPDATE}`
        : `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.NOTIFICATIONS.SAVE}`;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (authToken) headers.Authorization = `Bearer ${authToken}`;
      const baseBody = {
        workspace_id: effectiveWorkspaceId,
        channel_id: channelId,
        is_notifications_enabled: !muteAll,
        apps_enabled: selectedApps ? [selectedApps] : [],
        is_post_enabled: emailNotifications.posts,
        is_mention_enabled: emailNotifications.mentions,
        is_task_enabled: emailNotifications.tasks,
        is_approval_enabled: emailNotifications.approvals,
        is_new_email_enabled: emailNotifications.posts,
        is_email_reply_enabled: emailNotifications.replies,
      };
      const body = isUpdate ? { id: settingsId, ...baseBody } : baseBody;
      const response = await fetch(url, {
        method: isUpdate ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `Failed to ${isUpdate ? 'update' : 'save'} (${response.status})`);
      }
      const data = (await response.json()) as RealTimeNotificationSettingsResponse;
      setHasSavedOnce(true);
      if (data.workspace_id) setWorkspaceIdFromApi(data.workspace_id);
      if (data.id) setSettingsId(data.id);
      showSuccess(
        isUpdate
          ? 'Realtime notification settings updated successfully'
          : 'Realtime notification settings saved successfully'
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : `Failed to ${isUpdate ? 'update' : 'save'} notification settings`;
      setSaveError(message);
      showError(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        py: 3,
      }}
    >
      <Box
        sx={{
          maxWidth: 900,
          width: '100%',
          mx: 'auto',
          px: 3,
          py: 3,
          backgroundColor: muiTheme.palette.background.paper,
          borderRadius: 2,
          border: `1px solid ${muiTheme.palette.divider}`,
          boxShadow: muiTheme.shadows[1],
        }}
      >
        {loadError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {loadError}
          </Typography>
        )}
        {/* Mute all Notification section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5,
            pb: 1,
            borderBottom: `1px solid ${muiTheme.palette.divider}`,
          }}
        >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: muiTheme.palette.text.primary,
          }}
        >
          Mute all Notification
        </Typography>
        <IconButton
          onClick={() => setMuteAll(!muteAll)}
          sx={{
            padding: 0,
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          {muteAll ? (
            <ToggleOn sx={{ fontSize: 48, color: muiTheme.palette.primary.main }} />
          ) : (
            <ToggleOff sx={{ fontSize: 48, color: muiTheme.palette.action.disabled }} />
          )}
        </IconButton>
        </Box>

        {/* Email Notification section */}
        <Box>
          {/* Collapsible header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 3,
              cursor: 'pointer',
            }}
            onClick={() => setEmailNotificationsOpen(!emailNotificationsOpen)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: muiTheme.palette.text.primary,
                }}
              >
                Email Notification
              </Typography>
            </Box>
            <IconButton
              size="small"
              sx={{
                color: muiTheme.palette.text.secondary,
              }}
            >
              {emailNotificationsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          {/* Collapsible content */}
          <Collapse in={emailNotificationsOpen}>
            <Box sx={{ pl: 2 }}>
              {/* Posts */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <DescriptionIcon
                    sx={{
                      color: muiTheme.palette.text.secondary,
                      fontSize: 24,
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      color: muiTheme.palette.text.primary,
                      fontWeight: 500,
                    }}
                  >
                    Posts
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => handleEmailNotificationChange('posts')}
                  disabled={muteAll}
                  sx={{
                    padding: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.5,
                    },
                  }}
                >
                  {emailNotifications.posts ? (
                    <ToggleOn sx={{ fontSize: 48, color: muiTheme.palette.primary.main }} />
                  ) : (
                    <ToggleOff sx={{ fontSize: 48, color: muiTheme.palette.action.disabled }} />
                  )}
                </IconButton>
              </Box>

              {/* Approvals */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckBoxIcon
                    sx={{
                      color: muiTheme.palette.text.secondary,
                      fontSize: 24,
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      color: muiTheme.palette.text.primary,
                      fontWeight: 500,
                    }}
                  >
                    Approvals
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => handleEmailNotificationChange('approvals')}
                  disabled={muteAll}
                  sx={{
                    padding: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.5,
                    },
                  }}
                >
                  {emailNotifications.approvals ? (
                    <ToggleOn sx={{ fontSize: 48, color: muiTheme.palette.primary.main }} />
                  ) : (
                    <ToggleOff sx={{ fontSize: 48, color: muiTheme.palette.action.disabled }} />
                  )}
                </IconButton>
              </Box>

              {/* Tasks */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ListIcon
                    sx={{
                      color: muiTheme.palette.text.secondary,
                      fontSize: 24,
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      color: muiTheme.palette.text.primary,
                      fontWeight: 500,
                    }}
                  >
                    Tasks
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => handleEmailNotificationChange('tasks')}
                  disabled={muteAll}
                  sx={{
                    padding: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.5,
                    },
                  }}
                >
                  {emailNotifications.tasks ? (
                    <ToggleOn sx={{ fontSize: 48, color: muiTheme.palette.primary.main }} />
                  ) : (
                    <ToggleOff sx={{ fontSize: 48, color: muiTheme.palette.action.disabled }} />
                  )}
                </IconButton>
              </Box>

              {/* Mentions */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography
                    sx={{
                      color: muiTheme.palette.text.secondary,
                      fontSize: 24,
                      fontWeight: 'bold',
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    @
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: muiTheme.palette.text.primary,
                      fontWeight: 500,
                    }}
                  >
                    Mentions
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => handleEmailNotificationChange('mentions')}
                  disabled={muteAll}
                  sx={{
                    padding: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.5,
                    },
                  }}
                >
                  {emailNotifications.mentions ? (
                    <ToggleOn sx={{ fontSize: 48, color: muiTheme.palette.primary.main }} />
                  ) : (
                    <ToggleOff sx={{ fontSize: 48, color: muiTheme.palette.action.disabled }} />
                  )}
                </IconButton>
              </Box>

              {/* Replies */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ReplyIcon
                    sx={{
                      color: muiTheme.palette.text.secondary,
                      fontSize: 24,
                      transform: 'scaleX(-1)',
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      color: muiTheme.palette.text.primary,
                      fontWeight: 500,
                    }}
                  >
                    Replies
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => handleEmailNotificationChange('replies')}
                  disabled={muteAll}
                  sx={{
                    padding: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.5,
                    },
                  }}
                >
                  {emailNotifications.replies ? (
                    <ToggleOn sx={{ fontSize: 48, color: muiTheme.palette.primary.main }} />
                  ) : (
                    <ToggleOff sx={{ fontSize: 48, color: muiTheme.palette.action.disabled }} />
                  )}
                </IconButton>
              </Box>

              {/* Apps */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 0,
                }}
              >
                {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}> */}
                  {/* <AppsIcon
                    sx={{
                      color: muiTheme.palette.text.secondary,
                      fontSize: 24,
                    }}
                  /> */}
                  {/* <Typography
                    variant="body1"
                    sx={{
                      color: muiTheme.palette.text.primary,
                      fontWeight: 500,
                      mr: 2,
                    }}
                  >
                    Apps
                  </Typography> */}
                  {/* <FormControl
                    size="small"
                    sx={{
                      minWidth: 350,
                      flex: 1,
                      maxWidth: 650,
                    }}
                  >
                    <InputLabel>Choose Apps</InputLabel>
                    <Select
                      value={selectedApps}
                      onChange={handleAppsChange}
                      label="Choose Apps"
                      disabled={muteAll || !emailNotifications.apps}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: muiTheme.palette.divider,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: muiTheme.palette.text.secondary,
                        },
                      }}
                    >
                      <MenuItem value="">Choose Apps</MenuItem>
                      <MenuItem value="all">All Apps</MenuItem>
                      //Add more app options as needed
                    </Select>
                  </FormControl> */}
                {/* </Box> */}
                {/* <IconButton
                  onClick={() => handleEmailNotificationChange('apps')}
                  disabled={muteAll}
                  sx={{
                    padding: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.5,
                    },
                  }}
                > */}
                  {/* {emailNotifications.apps ? (
                    <ToggleOn sx={{ fontSize: 48, color: muiTheme.palette.primary.main }} />
                  ) : (
                    <ToggleOff sx={{ fontSize: 48, color: muiTheme.palette.action.disabled }} />
                  )}
                </IconButton> */}
              </Box>
            </Box>
          </Collapse>
        </Box>

        {saveError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {saveError}
          </Typography>
        )}
        {/* Save / Update Button: "Save" on first visit, "Update" after first save */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 0.5,
            pt: 0,
          }}
        >
          <Button
            type="button"
            variant="contained"
            onClick={handleSaveOrUpdate}
            disabled={saving}
            sx={{
              backgroundColor: muiTheme.palette.primary.main,
              color: '#ffffff',
              textTransform: 'none',
              px: 2,
              py: 1,
              fontWeight: 600,
              '&:hover': {
                backgroundColor: muiTheme.palette.primary.dark,
              },
            }}
          >
            {saving ? 'Saving...' : hasSavedOnce ? 'Update' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default NotificationsTab;
